/**
 * 用户认证相关 API Hooks
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/api/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/stores/appStore';
import type {
  ChangePasswordDto,
  BindEmailDto,
  BindPhoneDto,
  EmailLoginDto,
  EmailRegisterDto,
  PhoneLoginDto,
  PasswordLoginDto,
  PhoneRegisterDto,
  RegisterDto,
  ResetPasswordEmailDto,
  ResetPasswordDto,
  SendBindEmailCodeDto,
  SendBindPhoneCodeDto,
  SendEmailCodeDto,
  SendRegisterEmailCodeDto,
  SendRegisterPhoneCodeDto,
  SendResetPasswordEmailDto,
  SendResetPasswordSmsDto,
  SmsCaptchaChallengeDto,
  SendSmsCodeDto,
  SendSmsCodeResponse,
  SetPasswordDto,
  SmsCaptchaChallengeResponse,
  SmsCaptchaVerifyDto,
  SmsCaptchaVerifyResponse,
  RegisterContactCheckDto,
  RegisterContactCheckResponse,
  UsernameCheckResponse,
  User,
  AuthResponse,
  UpdateUserDto,
  UpdateUsernameDto,
  UploadAvatarDto,
} from '@/types/user';

// Query Keys
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
};

/**
 * 创建短信图形验证码 challenge Hook
 */
export function useCreateSmsCaptchaChallenge() {
  return useMutation<SmsCaptchaChallengeResponse, Error, SmsCaptchaChallengeDto>({
    mutationFn: (data) => authApi.createSmsCaptchaChallenge(data),
  });
}

/**
 * 校验短信图形验证码 Hook
 */
export function useVerifySmsCaptchaChallenge() {
  return useMutation<SmsCaptchaVerifyResponse, Error, SmsCaptchaVerifyDto>({
    mutationFn: (data) => authApi.verifySmsCaptchaChallenge(data),
  });
}

/**
 * 发送短信验证码 Hook
 */
export function useSendSmsCode() {
  return useMutation<SendSmsCodeResponse, Error, SendSmsCodeDto>({
    mutationFn: (data) => authApi.sendSmsCode(data),
  });
}

/**
 * 发送手机号注册验证码 Hook
 */
export function useSendRegisterPhoneCode() {
  return useMutation<SendSmsCodeResponse, Error, SendRegisterPhoneCodeDto>({
    mutationFn: (data) => authApi.sendRegisterPhoneCode(data),
  });
}

/**
 * 手机号登录 Hook
 */
export function usePhoneLogin() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, Error, PhoneLoginDto>({
    mutationFn: (data) => authApi.loginWithPhone(data),
    onSuccess: (data) => {
      // 登录成功后更新用户缓存
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });
}

/**
 * 用户名密码登录 Hook
 */
export function usePasswordLogin() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, Error, PasswordLoginDto>({
    mutationFn: (data) => authApi.loginWithPassword(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });
}

/**
 * 邮箱密码登录 Hook
 */
export function useEmailLogin() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, Error, EmailLoginDto>({
    mutationFn: (data) => authApi.loginWithEmail(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });
}

/**
 * 账号密码注册 Hook
 */
export function useRegister() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, Error, RegisterDto>({
    mutationFn: (data) => authApi.register(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });
}

/**
 * 手机号注册 Hook
 */
export function useRegisterWithPhone() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, Error, PhoneRegisterDto>({
    mutationFn: (data) => authApi.registerWithPhone(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });
}

/**
 * 邮箱注册 Hook
 */
export function useRegisterWithEmail() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, Error, EmailRegisterDto>({
    mutationFn: (data) => authApi.registerWithEmail(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });
}

/**
 * 发送邮箱验证码 Hook
 */
export function useSendEmailCode() {
  return useMutation<SendSmsCodeResponse, Error, SendEmailCodeDto>({
    mutationFn: (data) => authApi.sendEmailCode(data),
  });
}

/**
 * 发送邮箱注册验证码 Hook
 */
export function useSendRegisterEmailCode() {
  return useMutation<SendSmsCodeResponse, Error, SendRegisterEmailCodeDto>({
    mutationFn: (data) => authApi.sendRegisterEmailCode(data),
  });
}

/**
 * 注册用户名实时查重 Hook
 */
export function useCheckUsername() {
  return useMutation<UsernameCheckResponse, Error, string>({
    mutationFn: (username) => authApi.checkUsername(username),
  });
}

/**
 * 注册手机号/邮箱查重 Hook
 */
export function useCheckRegisterContact() {
  return useMutation<RegisterContactCheckResponse, Error, RegisterContactCheckDto>({
    mutationFn: (data) => authApi.checkRegisterContact(data),
  });
}

/**
 * 设置初始密码 Hook
 */
export function useSetPassword() {
  const queryClient = useQueryClient();

  return useMutation<User, Error, SetPasswordDto>({
    mutationFn: (data) => authApi.setPassword(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data);
      useAppStore.getState().setUser(data);
    },
  });
}

/**
 * 修改密码 Hook
 */
export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation<User, Error, ChangePasswordDto>({
    mutationFn: (data) => authApi.changePassword(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data);
      useAppStore.getState().setUser(data);
    },
  });
}

/**
 * 发送找回密码短信 Hook
 */
export function useSendResetPasswordSms() {
  return useMutation<SendSmsCodeResponse, Error, SendResetPasswordSmsDto>({
    mutationFn: (data) => authApi.sendResetPasswordSms(data),
  });
}

/**
 * 发送找回密码邮箱 Hook
 */
export function useSendResetPasswordEmail() {
  return useMutation<SendSmsCodeResponse, Error, SendResetPasswordEmailDto>({
    mutationFn: (data) => authApi.sendResetPasswordEmail(data),
  });
}

/**
 * 找回密码并自动登录 Hook
 */
export function useResetPassword() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, Error, ResetPasswordDto>({
    mutationFn: (data) => authApi.resetPassword(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });
}

/**
 * 邮箱找回密码并自动登录 Hook
 */
export function useResetPasswordWithEmail() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, Error, ResetPasswordEmailDto>({
    mutationFn: (data) => authApi.resetPasswordWithEmail(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });
}

/**
 * 退出登录 Hook
 */
export function useLogout() {
  const { logout } = useAuth();

  return useMutation<void, Error, void>({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      // 使用 AuthContext 统一清除所有认证状态
      logout();
    },
  });
}

/**
 * 获取当前用户信息 Hook
 */
export function useCurrentUser(options?: { enabled?: boolean }) {
  return useQuery<User, Error>({
    queryKey: authKeys.user(),
    queryFn: () => authApi.getCurrentUser(),
    retry: false,
    staleTime: 1000 * 60 * 5, // 5分钟内不重新获取
    enabled: options?.enabled ?? true, // 默认启用
  });
}

/**
 * 更新用户信息 Hook
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation<User, Error, UpdateUserDto>({
    mutationFn: (data) => authApi.updateProfile(data),
    onSuccess: (data) => {
      // 更新成功后更新用户缓存
      queryClient.setQueryData(authKeys.user(), data);
      useAppStore.getState().setUser(data);
    },
  });
}

/**
 * 修改用户名 Hook
 */
export function useUpdateUsername() {
  const queryClient = useQueryClient();

  return useMutation<User, Error, UpdateUsernameDto>({
    mutationFn: (data) => authApi.updateUsername(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data);
      useAppStore.getState().setUser(data);
    },
  });
}

/**
 * 上传头像 Hook
 */
export function useUploadAvatar() {
  return useMutation<string, Error, UploadAvatarDto>({
    mutationFn: (data) => authApi.uploadAvatar(data),
  });
}

/**
 * 绑定邮箱 Hook
 */
export function useBindEmail() {
  const queryClient = useQueryClient();

  return useMutation<User, Error, BindEmailDto>({
    mutationFn: (data) => authApi.bindEmail(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data);
      useAppStore.getState().setUser(data);
    },
  });
}

/**
 * 发送绑定邮箱验证码 Hook
 */
export function useSendBindEmailCode() {
  return useMutation<SendSmsCodeResponse, Error, SendBindEmailCodeDto>({
    mutationFn: (data) => authApi.sendBindEmailCode(data),
  });
}

/**
 * 绑定手机号 Hook
 */
export function useBindPhone() {
  const queryClient = useQueryClient();

  return useMutation<User, Error, BindPhoneDto>({
    mutationFn: (data) => authApi.bindPhone(data),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), data);
      useAppStore.getState().setUser(data);
    },
  });
}

/**
 * 发送绑定手机号验证码 Hook
 */
export function useSendBindPhoneCode() {
  return useMutation<SendSmsCodeResponse, Error, SendBindPhoneCodeDto>({
    mutationFn: (data) => authApi.sendBindPhoneCode(data),
  });
}
